import React, { useCallback, useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import ps from './Pallets.module.css'
import styles from './StockSettings.module.css'
import { apiJson } from '../../lib/api'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { fmtStamp } from './model'

// Stock & Dispatch settings → Establishment (§7n.8; V4 seEst): the tenant's
// own identity on export paperwork, one record, name and registration
// number. It is a single record, never a list, so there is nothing to add,
// archive or remove: it is viewed, and edited in place. The registration
// number is optional (EST-1, provisional). A save carries the record as the
// form opened it; the API refuses one over someone else's change.

export interface Establishment {
  name: string
  registration: string | null
  updated: { by: string | null; at: string } | null
}

export const EST_MAX = { name: 120, registration: 60 }

const tidy = (s: string) => s.trim().replace(/\s+/g, ' ')

// The form's own checks, in the API's words; the API checks again.
export function establishmentErrors(name: string, registration: string): string[] {
  const out: string[] = []
  if (!tidy(name)) out.push('Enter the establishment name.')
  else if (tidy(name).length > EST_MAX.name) out.push(`Keep the name to ${EST_MAX.name} characters.`)
  if (tidy(registration).length > EST_MAX.registration) out.push(`Keep the registration number to ${EST_MAX.registration} characters.`)
  return out
}

const errText = (e: any) => e?.body?.error || e?.message || String(e)

// V4's note, in our words: nothing here certifies the transfer record.
const COMPLIANCE_NOTE =
  'Regulatory fields for the transfer record must come from the forms Cordoma supplies. Control does not certify compliance, and export labels and the transfer record are not deployed yet, so nothing prints this record today.'

export function EstablishmentTab({ user }: { user: any }) {
  const [rec, setRec] = useState<Establishment | null | undefined>(undefined)
  const [loadError, setLoadError] = useState('')
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState<Establishment | null>(null)
  const [name, setName] = useState('')
  const [registration, setRegistration] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  const [toast, setToast] = useState('')
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid

  const load = useCallback(async () => {
    if (!userRef.current) return
    try {
      setRec((await apiJson<{ establishment: Establishment | null }>(userRef.current, '/api/settings/establishment')).establishment)
      setLoadError('')
    } catch (e) {
      setLoadError(`Failed to load the establishment: ${errText(e)}`)
    }
  }, [])

  useEffect(() => {
    if (uid) load()
  }, [uid, load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const edit = () => {
    setOpened(rec || null)
    setName(rec?.name || '')
    setRegistration(rec?.registration || '')
    setErrors([])
    setEditing(true)
  }

  const save = async () => {
    const local = establishmentErrors(name, registration)
    if (local.length) {
      setErrors(local)
      return
    }
    setBusy(true)
    try {
      await apiJson(userRef.current, '/api/settings/establishment', {
        method: 'PUT',
        body: { name, registration, before: opened ? { name: opened.name, registration: opened.registration } : null },
      })
      setEditing(false)
      setErrors([])
      setToast('Establishment saved.')
      await load()
    } catch (e: any) {
      const b = e?.body
      if (b?.code === 'NO_CHANGE') {
        setEditing(false)
        setErrors([])
        setToast('Nothing has changed.')
      } else if (b?.code === 'STALE') {
        // Show the newer record before anyone saves over it.
        setEditing(false)
        setErrors([b.error])
        await load()
      } else setErrors(b?.errors || [errText(e)])
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className={styles.note}>Your identity on export paperwork: one record per workspace.</div>
      {loadError && (
        <div className={styles.error} role="alert">
          {loadError}
        </div>
      )}
      {rec === undefined && !loadError && <div className={styles.emptyRow}>Loading…</div>}
      {rec !== undefined && !editing && (
        <div className={styles.infoCard} data-testid="establishment-view">
          {rec ? (
            <>
              <div className={styles.destName}>{rec.name}</div>
              <div className={styles.infoText}>{rec.registration ? 'Registration ' + rec.registration : 'No registration number recorded'}</div>
              {rec.updated && <div className={styles.destUse}>{'Last changed by ' + (rec.updated.by || 'unknown') + ' · ' + fmtStamp(rec.updated.at)}</div>}
            </>
          ) : (
            <div className={styles.infoEmpty}>No establishment recorded yet.</div>
          )}
          <div className={styles.destUse}>{COMPLIANCE_NOTE}</div>
          <div className={ps.actions}>
            <button type="button" className={pa.ghostBtn} onClick={edit}>
              {rec ? 'Edit' : 'Add the establishment'}
            </button>
          </div>
        </div>
      )}
      {editing && (
        <div className={styles.formCard} data-testid="establishment-form">
          <div className={styles.formTitle}>{opened ? 'Edit establishment' : 'Add the establishment'}</div>
          <div className={inertClass(busy, slow)}>
            <label className={styles.formField}>
              <span className={ps.fieldLabel}>Establishment name</span>
              <input className={ps.input} value={name} onChange={(e) => setName(e.target.value)} aria-label="Establishment name" autoFocus />
            </label>
            <label className={styles.formField}>
              <span className={ps.fieldLabel}>Registration number (optional)</span>
              <input className={ps.input} value={registration} onChange={(e) => setRegistration(e.target.value)} aria-label="Registration number" />
            </label>
            {errors.map((m) => (
              <div key={m} className={styles.error} role="alert">
                {m}
              </div>
            ))}
          </div>
          <div className={ps.actions}>
            <PendingButton className={pa.primaryBtn} busy={busy} pending="Saving…" onClick={save}>
              Save
            </PendingButton>
            <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {!editing &&
        errors.map((m) => (
          <div key={m} className={styles.error} role="alert">
            {m}
          </div>
        ))}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}
